import { useEffect } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { ArrowRight, ArrowLeft, Sparkles, Search } from "lucide-react";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { getCategory, CATEGORIES } from "../lib/categories";

// Browse page — opens when the user clicks a category on Home.
//
// Critical UX rule (per spec): clicking a category opens this page; it does
// NOT start an AI search. Only when the user picks one of these suggestions
// do we navigate to /chat with that exact query and trigger the LLM.
export default function Browse() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const category = getCategory(slug);

  // If somebody hits /browse/<unknown> directly, redirect to Home instead of
  // showing a dead-end. Other categories are still discoverable via Home.
  useEffect(() => {
    if (!category) navigate("/", { replace: true });
  }, [category, navigate]);

  if (!category) return null;
  const Icon = category.icon;

  const startSearch = (query) => {
    navigate("/chat", { state: { initial: query } });
  };

  return (
    <div className="min-h-screen flex flex-col bg-background relative overflow-hidden">
      {/* Ambient background — matches Home but more subtle */}
      <div className="absolute inset-0 grid-pattern opacity-40 pointer-events-none" />
      <div className="absolute inset-x-0 top-0 h-[480px] radial-fade pointer-events-none" />

      <div className="relative z-10">
        <Header />
      </div>

      <main className="relative z-10 flex-1 px-5 pt-8 sm:pt-12 pb-20">
        <div className="max-w-4xl mx-auto">
          {/* Back link */}
          <Link
            to="/"
            data-testid="browse-back-home"
            className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition mb-6 animate-float-up"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to home
          </Link>

          {/* Hero */}
          <div className="flex items-start gap-4 sm:gap-5 mb-3 animate-float-up stagger-1">
            <div className="w-14 h-14 sm:w-16 sm:h-16 shrink-0 rounded-2xl bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center">
              <Icon className="w-7 h-7 sm:w-8 sm:h-8 text-emerald-500" strokeWidth={1.6} />
            </div>
            <div className="min-w-0">
              <div className="text-[11px] uppercase tracking-widest text-emerald-500 font-semibold mb-1">
                {category.label}
              </div>
              <h1
                data-testid="browse-heading"
                className="font-display font-bold text-3xl sm:text-4xl md:text-5xl tracking-tight text-foreground leading-tight"
              >
                Popular {category.label} searches
              </h1>
              <p className="text-sm sm:text-base text-muted-foreground mt-2 max-w-2xl leading-relaxed">
                {category.blurb} Pick one to get a clear verdict, the best
                Amazon UK option, pros, cons and honest alternatives.
              </p>
            </div>
          </div>

          {/* Search suggestions grid */}
          <div className="mt-8 sm:mt-10 grid grid-cols-1 sm:grid-cols-2 gap-3 animate-float-up stagger-2">
            {category.searches.map((query, i) => (
              <button
                key={query}
                onClick={() => startSearch(query)}
                data-testid={`browse-search-${i}`}
                className="hover-lift group flex items-center gap-4 text-left bg-card border border-border hover:border-emerald-500/40 hover:bg-emerald-500/[0.03] rounded-2xl p-4 sm:p-5 transition"
              >
                <div className="w-10 h-10 shrink-0 rounded-xl bg-secondary border border-border group-hover:bg-emerald-500/10 group-hover:border-emerald-500/30 flex items-center justify-center transition">
                  <Search className="w-4 h-4 text-muted-foreground group-hover:text-emerald-500 transition" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-base text-foreground group-hover:text-foreground truncate">
                    {query}
                  </div>
                  <div className="text-xs text-muted-foreground mt-0.5">
                    Get an honest verdict
                  </div>
                </div>
                <ArrowRight className="w-4 h-4 text-muted-foreground group-hover:text-emerald-500 group-hover:translate-x-0.5 transition shrink-0" />
              </button>
            ))}
          </div>

          {/* Other categories */}
          <section className="mt-12 sm:mt-14 animate-float-up stagger-3">
            <div className="flex items-center gap-2 mb-3">
              <Sparkles className="w-4 h-4 text-emerald-500" />
              <span className="text-[11px] uppercase tracking-widest text-muted-foreground font-semibold">
                Other categories
              </span>
            </div>
            <div className="flex flex-wrap gap-2">
              {CATEGORIES.filter((c) => c.slug !== category.slug).map((c) => {
                const COtherIcon = c.icon;
                return (
                  <Link
                    key={c.slug}
                    to={`/browse/${c.slug}`}
                    data-testid={`browse-other-${c.slug}`}
                    className="inline-flex items-center gap-2 text-xs sm:text-sm px-3.5 py-2 rounded-full bg-card border border-border hover:border-emerald-500/40 hover:bg-emerald-500/5 text-muted-foreground hover:text-foreground transition"
                  >
                    <COtherIcon className="w-3.5 h-3.5 text-emerald-500" strokeWidth={2} />
                    {c.label}
                  </Link>
                );
              })}
            </div>
          </section>
        </div>
      </main>

      <footer className="relative z-10">
        <Footer />
      </footer>
    </div>
  );
}
